import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { apiInstance, gql } from "@/services/httpAdapter.ts";
import { IBasicPaginatedQueryResponse } from "@Common/api/types/pagination";
import { ICitActivity, ICitExportHistoryEntries } from "@Common/types/ContractsInTransit/model";
import { errorLogger } from "@Common/helpers/logger.ts";
import { IGetSystemSettingsScheduledAccountNumbers } from "@Settings/api/SystemSettings/queries.ts";

// Fragments
import { CitActivityFragment } from "./fragments.ts";

export const getCitExportHistory = async (locationId?: string, commissionCycleId?: string, payPeriodId?: string, exportId?: string) => {
	const query = gql`
		query getCitExportHistory($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getCitExportHistory(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				_id
				locationId
				commissionCycleId
				payPeriodId
				exportId
				exportType
				activities {
					_id
					recommendedAction
					isExportable
					exported {
						exportedOn
						exportedBy
						balance
					}
					funded
					control
					account
					description
					dealNumber
					age
					vinNumber
					balance
					source
					postingDate
					customerName
					customerNumber
					transactionDate
					hierarchy
					reservesHierarchy
					activityType
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		exportId,
	};

	try {
		const response: GraphqlQueryResponse<"getCitExportHistory", ICitExportHistoryEntries[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getCitExportHistory;
	} catch (err) {
		errorLogger(err as Error, { function: "getCitExportHistory", message: "Failed to get CIT data", variables });
		return null;
	}
};

interface IUploadCitArgs {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	file: File;
}

export async function uploadCit({ locationId, commissionCycleId, payPeriodId, file }: IUploadCitArgs) {
	try {
		if (!locationId || !commissionCycleId || !payPeriodId) {
			return {
				status: false,
				message: `Can't upload file without a locationId, a commissionCycleId or a payPeriodId`,
			};
		}
		const SUCCESS_STATUS_CODE = 200;
		const query = gql`
			query getCitPresignedUrl($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
				getCitPresignedUrl(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
					fileName
					presignedUrl
				}
			}
		`;

		const variables = {
			locationId,
			commissionCycleId,
			payPeriodId,
		};

		const response = await apiInstance.post("/graphql", { query, variables });
		const presigned = response.data?.data?.getCitPresignedUrl;
		if (!presigned) {
			return {
				status: false,
				message: `Failed to get presigned URL`,
			};
		}

		const fileName = presigned?.fileName;
		const uploadFile = new File([file], fileName);

		const options = {
			headers: {
				"Content-Type": file.type,
			},
		};
		const upload = await apiInstance.put(presigned?.presignedUrl, uploadFile, options);
		if (upload.status === SUCCESS_STATUS_CODE) {
			return {
				status: true,
				message: `Uploaded successfully`,
			};
		}

		return {
			status: false,
			message: `Some error occurred`,
		};
	} catch (error) {
		errorLogger(error as Error, { function: "uploadCit", message: "Failed to upload CIT file" });
		return {
			status: false,
			message: `${(error as { message?: string })?.message}`,
		};
	}
}

export interface IGetCITMaxValuesResponse {
	citMaxGraphValues: {
		maxUnfundedDollars: number;
		maxUnfundedContracts: number;
	};
	citMinimumBalance: number;
	citGraphWidth: string;
}

export async function getCITMaxValues(locationId?: string) {
	const query = gql`
		query getSystemSettings($locationId: String!) {
			getSystemSettings(locationId: $locationId) {
				citMaxGraphValues {
					maxUnfundedDollars
					maxUnfundedContracts
				}
				citMinimumBalance
				citGraphWidth
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getSystemSettings", IGetCITMaxValuesResponse> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getSystemSettings;
	} catch (err) {
		errorLogger(err as Error, { function: "getCITMaxValues", message: "Failed to get CIT max values", variables });
		return null;
	}
}

type TGetCITSystemSettingsResponse = IGetCITMaxValuesResponse & IGetSystemSettingsScheduledAccountNumbers;

interface IGetCITAdministrationToolsResponse {
	integrations: {
		dmsSources: {
			_id: string;
			label: string;
			value: string;
		}[];
	};
}

export type TGetCitSystemSettingsResponse = GraphqlQueryResponse<"getSystemSettings", TGetCITSystemSettingsResponse> &
	GraphqlQueryResponse<"getAdministrationTools", IGetCITAdministrationToolsResponse>;

export async function getCITSystemSettings(locationId?: string) {
	const query = gql`
		query getSystemSettings($locationId: String!) {
			getSystemSettings(locationId: $locationId) {
				_id
				citMaxGraphValues {
					maxUnfundedDollars
					maxUnfundedContracts
				}
				citMinimumBalance
				citMaxScheduleDays
				citScheduledAccountNumbers {
					label
					values
				}
				citGraphWidth
				citEmailDistribution {
					accountNumbers {
						sourceAccount
						sourceLabelId
					}
				}
			}
			getAdministrationTools(locationId: $locationId) {
				integrations {
					dmsSources {
						_id
						label
						value
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: TGetCitSystemSettingsResponse = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		if (!response.data.data?.getAdministrationTools || !response.data.data?.getSystemSettings) {
			throw new Error("Error fetching CIT system settings");
		}

		return {
			...response.data.data.getSystemSettings,
			dmsSources: response.data.data.getAdministrationTools.integrations.dmsSources,
		};
	} catch (err) {
		errorLogger(err as Error, { function: "getCITMaxValues", message: "Failed to get CIT max values", variables });
		return null;
	}
}

export async function getCitActivities(
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	first: number,
	after = ""
): Promise<IBasicPaginatedQueryResponse<ICitActivity> | null> {
	const query = gql`
		query getCitActivities($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $pagination: PaginationInput) {
			getCitActivities(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				pagination: $pagination
			) {
				items {
					...CitActivity
				}
				pageInfo {
					hasNextPage
					endCursor
				}
			}
		}
		${CitActivityFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		pagination: {
			first,
			after,
		},
	};

	try {
		const response: GraphqlQueryResponse<"getCitActivities", IBasicPaginatedQueryResponse<ICitActivity>> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getCitActivities ?? null;
	} catch (err) {
		errorLogger(err as Error, { function: "getCitActivities", message: "Failed to get CIT data", variables });
		return null;
	}
}
